<template>
  <div class="__input-number" :style="{ width: width }">
    <div class="label-section" v-if="$slots.label || label || description">
      <slot name="label" v-if="$slots.label" />

      <div class="label" v-if="label && !$slots.label">
        <div>
          {{ label }}
          <span class="danger-color" v-if="required">*</span>
          <span v-if="optional">{{ $t('(Optional)') }}</span>
        </div>
      </div>

      <slot name="description" />
      <div class="description" v-if="description">{{ description }}</div>
    </div>

    <!-- Number -->
    <q-input
      style="width: 100%"
      ref="input"
      type="number"
      v-model.number="currentValue"
      :placeholder="placeholder"
      :hint="hint"
      :disable="disabled"
      :prefix="currentPrefix"
      :suffix="suffix"
      :readonly="readonly"
      :debounce="debounce"
      bottom-slots
      :error="isInValid"
      outlined
      dense
      :hide-bottom-space="hideBottomSpace"
      no-error-icon
      :max="max"
      :min="min"
      bg-color="white"
      @change="handleChange($event)"
    >
      <template v-slot:error>
        {{ $t(errorMsg) }}
      </template>

      <template v-slot:after>
        <slot name="after" />
      </template>
    </q-input>
  </div>
</template>

<script>
/*
 * Using 'vuelidate' for validate
 * https://vuelidate.js.org/
 */

import { validationMixin } from 'vuelidate';
import { required, minValue, maxValue, between, integer } from 'vuelidate/lib/validators';
import { currencySymbol } from '@/lib/currency';

const messages = {
  en: {
    '(Optional)': '(Optional)',
  },
  ja: {
    '(Optional)': '（オプション）',
  },
};

export default {
  name: 'CInputNumber',
  i18n: { messages: messages },
  mixins: [validationMixin],
  validations() {
    return {
      currentValue: {
        required: this.required ? required : false,
        between: this.min && this.max ? between(this.min, this.max) : false,
        minValue: this.min ? minValue(this.min) : false,
        maxValue: this.max ? maxValue(this.max) : false,
        integer: this.integer ? integer : false,
      },
    };
  },
  props: {
    value: {
      type: [String, Number],
      default: 0,
    },
    name: {
      type: String,
    },
    label: {
      type: String,
      default: '',
    },
    description: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    hint: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    debounce: {
      type: Number,
      default: 0,
    },
    // Validate

    // .sync
    isInvalid: {
      type: Boolean,
      default: false,
    },

    required: {
      type: Boolean,
      default: false,
    },

    optional: {
      type: Boolean,
    },

    min: {
      type: Number,
    },

    max: {
      type: Number,
    },

    integer: {
      type: Boolean,
    },

    currency: {
      type: Boolean,
    },

    // Layout
    prefix: {
      type: String,
    },

    suffix: {
      type: String,
    },

    width: {
      type: String,
      default: '100%',
    },

    hideBottomSpace: {
      type: Boolean,
    },

    noErrorIcon: {
      type: Boolean,
    },
  },
  watch: {
    currentValue() {
      this.$v['currentValue'].$touch();
    },
  },
  computed: {
    currentValue: {
      set(value) {
        if (this.value === null || this.value === '') {
          value = 0;
        }

        this.$emit('input', value);
        //this.$emit('change', value);
      },
      get() {
        return this.value === null || this.value === '' ? 0 : this.value;
      },
    },

    currentPrefix() {
      if (this.currency) {
        return currencySymbol(this.prefix);
      }

      return this.prefix;
    },

    isInValid() {
      if (this.$v['currentValue'].$dirty) {
        const isInvalid = this.$v['currentValue'].$invalid;

        // Use for .sync
        this.$emit('update:isInvalid', isInvalid);

        return isInvalid;
      } else {
        // Use for .sync
        this.$emit('update:isInvalid', false);

        return false;
      }
    },

    errorMsg() {
      const target = this.$v['currentValue'];

      // Priority order↓

      // Required
      if (this.required) {
        let msg;

        if (this.simple) {
          msg = this.$t('Required');
        } else {
          msg = this.$t('{name} is required.', { name: this.name ? this.name : this.label });
        }

        if (!target.required) return msg;
      }

      // Between number
      if (typeof this.min === 'number' && typeof this.max === 'number') {
        if (!target.between)
          return this.$t('{name} must be between {min} and {max}.', {
            name: this.name ? this.name : this.label,
            min: this.min,
            max: this.max,
          });
      }

      // Min value
      if (typeof this.min === 'number') {
        let msg;

        if (this.simple) {
          msg = this.$t('{number} or more', { number: this.min });
        } else {
          msg = this.$t('Please enter a number greater than {number}.', { number: this.min });
        }

        if (!target.minValue) return msg;
      }

      // Max value
      if (typeof this.max === 'number') {
        let msg;

        if (this.simple) {
          msg = this.$t('{number} or less', { number: this.max });
        } else {
          msg = this.$t('Please enter a number smaller than {number}.', { number: this.max });
        }

        if (!target.maxValue) return msg;
      }

      // Integer
      if (!target.integer) return this.$t('Please enter an integer.');

      return '';
    },
  },
  methods: {
    focus() {
      this.$nextTick(() => {
        this.$refs['input'].focus();
      });
    },

    validate() {
      this.$v['currentValue'].$touch();

      if (this.isInValid) return Promise.reject('invalid');
      else return Promise.resolve();
    },

    handleChange() {
      this.$emit('change', this.currentValue);
    },
  },
};
</script>

<style lang="scss" scoped>
.__input-number {
  position: relative;
}

.label-section {
  margin-bottom: $margin-xs;

  .label {
    display: flex;
    align-items: center;

    .badge {
      margin-left: $margin-sm;
    }
  }
}

.description {
  font-size: $font-size-sm;
  color: $neutral-secondary-color;
}
</style>
